import { ACTION_LABEL } from "@/lib/i18nLabels";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, ShieldAlert, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatDetail } from "@/lib/api";
import { formatIDR } from "@/lib/constants";
import RupiahInput from "@/components/RupiahInput";

/**
 * PettyCashSettingsDialog — dialog per-user untuk mengatur:
 *   1. Akses Petty Cash (Matrix default / Grant paksa / Revoke paksa)
 *   2. Maksimal Saldo Petty Cash (Rupiah — kosongkan = tanpa batas)
 *
 * Backend endpoint: PATCH /api/admin/users/{uid}/petty-cash-settings
 * BOD & HRGA only (403 for others).
 */
export default function PettyCashSettingsDialog({ open, onOpenChange, user, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [accessMode, setAccessMode] = useState("matrix"); // "matrix" | "grant" | "revoke"
  const [maxAmount, setMaxAmount] = useState(""); // string for input; empty = unlimited

  useEffect(() => {
    if (!open || !user) return;
    setLoading(true);
    api.get(`/admin/users/${user.id}/petty-cash-settings`)
      .then(({ data }) => {
        setSettings(data);
        // Derive current access state
        if (!data.has_override) setAccessMode("matrix");
        else if (data.enabled) setAccessMode("grant");
        else setAccessMode("revoke");
        setMaxAmount(data.max_amount ? String(data.max_amount) : "");
      })
      .catch((e) => toast.error(formatDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return;
    setBusy(true);
    try {
      // Map accessMode → payload.enabled
      const payload = { max_amount: null };
      if (accessMode === "grant") payload.enabled = true;
      else if (accessMode === "revoke") payload.enabled = false;
      else payload.enabled = null; // clear override
      // Max amount: empty or 0 → null (unlimited)
      const num = parseFloat(String(maxAmount).replace(/[^0-9.]/g, ""));
      payload.max_amount = isNaN(num) || num <= 0 ? null : num;
      await api.patch(`/admin/users/${user.id}/petty-cash-settings`, payload);
      toast.success(`Pengaturan Petty Cash ${user.name} disimpan`);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const isBODLv1 = user?.division === "BOD" && user?.level === 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="dialog-pc-settings">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Wallet size={18} className="text-emerald-700" />
            Pengaturan Petty Cash
          </DialogTitle>
          <DialogDescription>
            {user && (
              <span>
                <span className="font-semibold text-[#0B1D3A]">{user.name}</span>
                <span className="text-slate-500"> · {user.division}{user.sub_division ? ` / ${user.sub_division}` : ""} · Lv{user.level}</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-6 text-center text-slate-400 text-xs">Memuat pengaturan…</div>
        ) : isBODLv1 ? (
          <div className="p-4 border-2 border-slate-300 bg-slate-50 text-xs space-y-2" data-testid="pc-settings-bod-notice">
            <div className="flex items-center gap-2 font-semibold text-[#0B1D3A]">
              <ShieldAlert size={14} /> Super Admin — Tidak Perlu Pengaturan
            </div>
            <p className="text-slate-600">
              BOD Level 1 selalu memiliki akses penuh ke Petty Cash tanpa batas. Pengaturan hanya berlaku untuk staff lain.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            {/* Access mode */}
            <div className="space-y-2">
              <Label className="overline text-slate-600">Akses Weekly Petty Cash</Label>
              <div className="grid grid-cols-3 gap-0 border border-slate-300" data-testid="pc-access-mode">
                <button
                  type="button"
                  data-testid="pc-mode-matrix"
                  onClick={() => setAccessMode("matrix")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-r border-slate-200 ${accessMode === "matrix" ? "bg-[#0B1D3A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  Matrix (Default)
                </button>
                <button
                  type="button"
                  data-testid="pc-mode-grant"
                  onClick={() => setAccessMode("grant")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-r border-slate-200 ${accessMode === "grant" ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-emerald-50"}`}
                >
                  Diberi Akses
                </button>
                <button
                  type="button"
                  data-testid="pc-mode-revoke"
                  onClick={() => setAccessMode("revoke")}
                  className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold ${accessMode === "revoke" ? "bg-rose-600 text-white" : "bg-white text-slate-600 hover:bg-rose-50"}`}
                >
                  Dicabut
                </button>
              </div>
              <div className="border border-slate-200 bg-slate-50 p-2.5 text-[11px] text-slate-600 flex gap-2" data-testid="pc-access-explain">
                <Info size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
                <div>
                  {accessMode === "matrix" && (
                    <>
                      Ikuti aturan matrix per divisi × level. Saat ini staff <b>{settings?.inherited_enabled ? "bisa" : "tidak bisa"}</b> ajukan petty cash by default.
                    </>
                  )}
                  {accessMode === "grant" && (
                    <>Staff diperbolehkan mengajukan Weekly Petty Cash meskipun matrix default menolak untuk divisi/level-nya.</>
                  )}
                  {accessMode === "revoke" && (
                    <>Staff <b>tidak bisa</b> mengajukan Weekly Petty Cash meskipun matrix default membolehkan.</>
                  )}
                </div>
              </div>
            </div>

            {/* Max amount */}
            <div className="space-y-2">
              <Label className="overline text-slate-600">Maksimal Saldo Petty Cash</Label>
              <RupiahInput
                testid="pc-max-amount"
                value={maxAmount}
                onChange={(v) => setMaxAmount(v)}
                placeholder="Kosongkan = tanpa batas"
                disabled={accessMode === "revoke"}
              />
              <div className="flex flex-wrap gap-1">
                {[500000, 1000000, 2000000, 5000000, 10000000].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`pc-preset-${v}`}
                    disabled={accessMode === "revoke"}
                    onClick={() => setMaxAmount(String(v))}
                    className={`text-[10px] px-2 py-0.5 border transition-colors ${Number(maxAmount) === v ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                  >
                    {formatIDR(v)}
                  </button>
                ))}
                <button
                  type="button"
                  data-testid="pc-preset-clear"
                  disabled={accessMode === "revoke"}
                  onClick={() => setMaxAmount("")}
                  className="text-[10px] px-2 py-0.5 border border-slate-300 text-slate-500 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Tanpa batas
                </button>
              </div>
              <p className="text-[10px] text-slate-500">
                Saldo petty cash staff (yang sudah ditransfer − yang sudah direalisasi & diverifikasi) tidak boleh melebihi angka ini saat FAT mentransfer pengajuan baru. Kosongkan atau nol = tanpa batas.
                {maxAmount && Number(maxAmount) > 0 && (
                  <span className="ml-1 font-mono text-amber-700">Saat ini: {formatIDR(Number(maxAmount))}</span>
                )}
              </p>
            </div>

            <DialogFooter className="border-t border-slate-200 pt-3 mt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>{ACTION_LABEL.cancel}</Button>
              <Button type="submit" data-testid="pc-settings-submit" disabled={busy} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13]">
                {busy ? "Menyimpan…" : "Simpan Pengaturan"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
